// @ts-check
// Rotas de painéis, widgets e exportação (docs/arquitetura-appcrm/07-contrato-paineis.md, seções 3 e 4).
import { api, requestFile } from './api-client.js';
import { serializeOrderBy, serializeRestFilter } from '../lib/rest-filter.js';

/**
 * @typedef {'TODAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'LAST_30_DAYS' | 'THIS_YEAR' | 'CUSTOM'} DashboardPeriod
 * @typedef {{ period?: DashboardPeriod, from?: string | null, to?: string | null, campusId?: string | null,
 *   ownerWorkspaceMemberId?: string | null, academicPeriodId?: string | null }} DashboardDefaultFilters
 * @typedef {{ from?: string, to?: string, campusId?: string, ownerWorkspaceMemberId?: string, academicPeriodId?: string }} DashboardRequestFilters
 * @typedef {'KPI' | 'AGGREGATE' | 'BAR' | 'LINE' | 'PIE' | 'TABLE' | 'TEXT' | 'IFRAME' | 'STAT_GROUP' | 'BAR_LIST' | 'DISTRIBUTION' | 'FUNNEL_FLOW' | 'STAGE_FLOW' | 'UPCOMING_TABLE' | 'GEO_MAP'} WidgetType
 * @typedef {{ id?: string, type: WidgetType, title: string, configuration: Record<string, any>,
 *   gridRow: number, gridColumn: number, rowSpan: number, columnSpan: number, tabId?: string | null }} DashboardWidget
 * @typedef {{ id: string, title: string, icon?: string | null, position?: number }} DashboardTab
 * @typedef {{ id: string, name: string, icon?: string | null, position: number, defaultFilters: DashboardDefaultFilters, widgetCount?: number,
 *   createdByWorkspaceMemberId?: string | null, canEdit?: boolean }} DashboardSummary
 * @typedef {DashboardSummary & { tabs: DashboardTab[], widgets: DashboardWidget[], redirectedFromId?: string }} Dashboard
 *   redirectedFromId: o id pedido era de um painel fundido neste (contrato 07, seção 12)
 *
 * @typedef {'NUMBER' | 'CURRENCY' | 'PERCENT' | 'DAYS'} NumberFormat
 * @typedef {{ kind: 'NUMBER', value: number | null, format: NumberFormat, previous?: number | null, goal?: number | null,
 *   goalProgress?: number | null, goalSince?: string | null, secondary?: { label: string, value: string | number | null, count?: number | null } | null }} NumberWidgetData
 * @typedef {{ key: string, label: string, values: (number | null)[], format?: NumberFormat, color?: string }} ChartSeries
 * @typedef {{ kind: 'SERIES', categories: { key: string, label: string }[], series: ChartSeries[], format: NumberFormat, hasTooManyGroups?: boolean }} SeriesWidgetData
 * @typedef {{ kind: 'PIE', slices: { key: string, label: string, value: number }[], total: number, format: NumberFormat, hasTooManyGroups?: boolean }} PieWidgetData
 * @typedef {'NUMBER' | 'CURRENCY' | 'PERCENT' | 'TEXT' | 'DATE' | 'DATE_TIME' | 'STATUS'} TableColumnFormat
 * @typedef {{ kind: 'TABLE', columns: { key: string, label: string, format?: TableColumnFormat, options?: { value: string, label: string, color?: string }[] }[],
 *   rows: { id?: string, href?: string | null, cells: Record<string, any>, linked?: Record<string, number> }[], totals?: Record<string, any> | null,
 *   linkedTotals?: Record<string, number>, report?: string }} TableWidgetData
 *   linked e linkedTotals: na ocupação das turmas, as matrículas vinculadas de cada coluna (o número do ERP pode ser maior)
 * @typedef {{ kind: 'TEXT', markdown: string }} TextWidgetData
 * @typedef {{ kind: 'IFRAME', url: string | null }} IframeWidgetData
 * @typedef {import('../modules/dashboards/report-widgets.js').StatGroupData | import('../modules/dashboards/report-widgets.js').BarListData
 *   | import('../modules/dashboards/report-widgets.js').DistributionData | import('../modules/dashboards/report-widgets.js').FunnelFlowData
 *   | import('../modules/dashboards/report-widgets.js').StageFlowData | import('../lib/geo-map.js').GeoMapData} ReportWidgetData
 * @typedef {NumberWidgetData | SeriesWidgetData | PieWidgetData | TableWidgetData | TextWidgetData | IframeWidgetData | ReportWidgetData} WidgetData
 */

/**
 * @param {any} body
 * @param {string} key
 */
function readData(body, key) {
  const data = body?.data ?? body;
  return data && typeof data === 'object' && key in data ? data[key] : data;
}

/**
 * @returns {Promise<DashboardSummary[]>}
 */
export async function listDashboards() {
  const dashboards = readData(await api.get('/rest/dashboards'), 'dashboards');
  return Array.isArray(dashboards) ? [...dashboards].sort((first, second) => (first.position ?? 0) - (second.position ?? 0)) : [];
}

/**
 * @param {string} id
 * @returns {Promise<Dashboard>}
 */
export async function getDashboard(id) {
  const dashboard = readData(await api.get(`/rest/dashboards/${id}`), 'dashboard');
  return { ...dashboard, defaultFilters: dashboard?.defaultFilters ?? {}, tabs: dashboard?.tabs ?? [], widgets: dashboard?.widgets ?? [] };
}

/**
 * @param {{ name: string, icon?: string, duplicateFromId?: string }} input
 * @returns {Promise<Dashboard>}
 */
export async function createDashboard(input) {
  return readData(await api.post('/rest/dashboards', input), 'dashboard');
}

/**
 * @param {string} id
 * @param {{ name?: string, icon?: string, position?: number, defaultFilters?: DashboardDefaultFilters }} patch
 */
export async function updateDashboard(id, patch) {
  return readData(await api.patch(`/rest/dashboards/${id}`, patch), 'dashboard');
}

/**
 * Salva abas e grade inteiras: widgets e abas sem id são criados e os ausentes, removidos. As abas novas vão pela
 * chave do rascunho, que os widgets usam em `tabId` (contrato 07, seção 8.1).
 * @param {string} id
 * @param {DashboardWidget[]} widgets
 * @param {({ id: string, title: string, icon?: string | null } | { key: string, title: string, icon?: string | null })[]} [tabs]
 * @returns {Promise<Dashboard>}
 */
export async function saveDashboardWidgets(id, widgets, tabs) {
  const body = await api.put(`/rest/dashboards/${id}/widgets`, {
    ...(tabs ? { tabs } : {}),
    widgets: widgets.map(({ id: widgetId, type, title, configuration, gridRow, gridColumn, rowSpan, columnSpan, tabId }) => ({
      ...(widgetId ? { id: widgetId } : {}),
      type,
      title,
      configuration,
      gridRow,
      gridColumn,
      rowSpan,
      columnSpan,
      ...(tabId ? { tabId } : {}),
    })),
  });
  return readData(body, 'dashboard');
}

/**
 * @param {string} id
 */
export async function deleteDashboard(id) {
  return api.delete(`/rest/dashboards/${id}`);
}

/**
 * Dados de um widget (salvo ou em rascunho) com os filtros atuais do painel.
 * @param {{ type: WidgetType, configuration: Record<string, any> }} widget
 * @param {DashboardRequestFilters} filters
 * @returns {Promise<WidgetData>}
 */
export async function fetchWidgetData(widget, filters) {
  const body = await api.post('/rest/dashboards/widget-data', { widget: { type: widget.type, configuration: widget.configuration }, filters });
  return body?.data ?? body;
}

/**
 * Clique num ponto do gráfico (contrato 07, seção 10): o servidor recalcula o ponto da receita com o papel de quem
 * clica e devolve a lista pronta para abrir (`url`), a contagem e o rótulo do chip.
 * @param {string} recipe receita em base64url (o `d` do link do ponto)
 */
export async function resolveDrilldown(recipe) {
  const body = await api.post('/rest/dashboards/drilldown', { recipe });
  return body?.data ?? body;
}

/**
 * Estado do chip da lista aberta por um clique (`f.<receita>` ou `s.<receita>`): rótulo, contagem e quando calculou.
 * @param {string} parameter
 */
export async function describeDrilldown(parameter) {
  const body = await api.get('/rest/dashboards/drilldown', { d: parameter });
  return body?.data ?? body;
}

/** @type {Map<string, Promise<import('../../../shared/geo/geo-mesh.js').GeoMesh>>} */
const geoMeshCache = new Map();

/**
 * Malha do IBGE do mapa de calor (contrato 07, seção 9): `BR` (UFs) ou o código da UF (municípios). Fica na memória
 * da página: a mesma malha serve a todos os cartões e às voltas pela trilha.
 * @param {string} parentCode
 */
export function fetchGeoMesh(parentCode) {
  const cached = geoMeshCache.get(parentCode);
  if (cached) return cached;
  const promise = api.get(`/rest/dashboards/geo-meshes/${encodeURIComponent(parentCode)}`).then((body) => readData(body, 'mesh'));
  geoMeshCache.set(parentCode, promise);
  promise.catch(() => geoMeshCache.delete(parentCode));
  return promise;
}

/**
 * CSV de uma lista gerado no servidor, com os filtros, a ordenação e os campos da view atual.
 * @param {string} objectNamePlural
 * @param {{ filter?: import('../lib/rest-filter.js').FilterNode | null, orderBy?: import('../lib/rest-filter.js').OrderByItem[],
 *   fields?: string[], viewId?: string | null, drilldown?: string | null }} options drilldown: a lista aberta por um clique num gráfico
 */
export async function downloadRecordsCsv(objectNamePlural, { filter, orderBy = [], fields = [], viewId, drilldown } = {}) {
  return requestFile(`/rest/${objectNamePlural}/export.csv`, {
    filter: serializeRestFilter(filter ?? null),
    order_by: serializeOrderBy(orderBy),
    fields: fields.join(','),
    viewId: viewId && !viewId.startsWith('fallback-') ? viewId : undefined,
    drilldown: drilldown ?? undefined,
  });
}
